import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiFormRow, EuiSpacer, EuiTitle } from '@elastic/eui'

import { SegmentTarget } from '../../../../../api/interfaces'
import { LocationTargetingType, LocationTargetingTypeToLocationType } from '../../../../../api/interfaces/CampaignTargeting'
import { useUpdateCampaignLocationTargetingMutation } from '../../../../../api/rtkQueryApi/platform/campaignsApi'
import { showSuccessToast } from '../../../../../app/appSlice'
import { RootState } from '../../../../../app/rootReducer'
import { AudienceTargetingForm } from '../../../../../components/targeting/TargetingForm/AudienceTargetingForm'
import { SegmentsForm } from '../../../../../components/targeting/TargetingForm/SegmentsForm'
import { GetAudienceTargeting, GetLocationTargeting } from '../../../../../utils/CampaignFormatter'
import { DEFAULT_AUDIENCE_TARGETING } from '../../../../../utils/TargetingFormDefaults'
import { fetchCampaign } from '../../campaignSlice'
import { useCampaignEdit } from '../CampaignEditContext'

export const EditAudienceTargeting = () => {
  const { campaign } = useSelector((state: RootState) => state.campaign)

  const [locationTargeting, setLocationTargeting] = useState(GetLocationTargeting(campaign ?? undefined))
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [updateTargeting] = useUpdateCampaignLocationTargetingMutation()
  const dispatch = useDispatch()
  const [audienceTargeting, setAudienceTargeting] = useState(GetAudienceTargeting(campaign ?? undefined))
  const { registerSave, markDirty } = useCampaignEdit()

  useEffect(() => {
    if (campaign) {
      setLocationTargeting(GetLocationTargeting(campaign))
      setAudienceTargeting(GetAudienceTargeting(campaign))
    }
  }, [campaign])

  const onSave = useCallback(async () => {
    if (!campaign || !currentAccount) {
      return
    }
    updateTargeting({
      campaignId: campaign.id,
      accountId: currentAccount?.id,
      targeting: {
        locations: {
          countries: [],
          regions: locationTargeting.targetingType === LocationTargetingType.Region ? locationTargeting.regions ?? [] : [],
          dmas: locationTargeting.targetingType === LocationTargetingType.DMA ? locationTargeting.dmas ?? [] : [],
          cities: locationTargeting.targetingType === LocationTargetingType.City ? locationTargeting.cities ?? [] : [],
          postalCodes: locationTargeting.targetingType === LocationTargetingType.Zip ? locationTargeting.zips ?? [] : [],
          uploadedAudienceUrl: locationTargeting.uploadedAudienceUrl
        },
        geoCircles: locationTargeting.targetingType === LocationTargetingType.Map ? locationTargeting.geoCircles ?? [] : [],
        geoRectangles: locationTargeting.targetingType === LocationTargetingType.Map ? locationTargeting.geoRectangles ?? [] : [],
        geoPolygons: locationTargeting.targetingType === LocationTargetingType.Map ? locationTargeting.geoPolygons ?? [] : [],
        geoFences:
          locationTargeting.targetingType === LocationTargetingType.Address
            ? (locationTargeting.geoFences ?? []).map(f => ({
                placeId: f.placeId,
                radiusUnits: f.radiusUnits,
                radius: f.radius,
                formattedAddress: f.formattedAddress,
                name: f.formattedAddress,
                lat: f.lat,
                lng: f.lng
              }))
            : []
      },
      locationType: LocationTargetingTypeToLocationType(locationTargeting.targetingType),
      audience: {
        ageRanges: audienceTargeting.demographics?.ageRanges ?? [],
        genders: audienceTargeting.demographics?.genders ?? [],
        householdIncomes: audienceTargeting.demographics?.householdIncomes ?? [],
        thirdPartyAudiences: [],
        segments: audienceTargeting.segments ?? []
      }
    }).then(() => {
      dispatch(showSuccessToast('Saved targeting information'))
      dispatch(fetchCampaign(currentAccount.id, campaign.id))
    })
  }, [campaign, currentAccount, locationTargeting, audienceTargeting, updateTargeting, dispatch])

  useEffect(() => {
    registerSave('audienceTargeting', onSave)
  }, [onSave, registerSave])

  const onSegmentsChange = (segments: SegmentTarget[]) => {
    setAudienceTargeting({
      ...audienceTargeting,
      segments
    })
    markDirty('audienceTargeting')
  }

  if (!campaign) {
    return null
  }

  return (
    <>
      <EuiTitle size={'xs'}>
        <h3>Segments</h3>
      </EuiTitle>
      <EuiSpacer size='xs' />
      <SegmentsForm campaignType={campaign.type} segments={audienceTargeting.segments} onSegmentsChange={onSegmentsChange} />
      <EuiSpacer />
      <EuiTitle size={'xs'}>
        <h3>Demographics</h3>
      </EuiTitle>
      <EuiSpacer size='xs' />
      <EuiFormRow fullWidth>
        <AudienceTargetingForm
          audienceTargeting={audienceTargeting ?? DEFAULT_AUDIENCE_TARGETING}
          onAudienceTargetingChanged={targeting => {
            setAudienceTargeting(targeting)
            markDirty('audienceTargeting')
          }}
        />
      </EuiFormRow>
    </>
  )
}
